import { Text } from '@mantine/core';
import { IconArchive, IconFiles, IconFolder } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import classes from './Documents.module.css';
import { localFolderLabel, otherDocumentIds } from './folder-tree.js';
import { folderDocumentCount, siblingsOf } from './office.js';
import type { LibraryFolder, LibrarySection, LibraryTree } from './types.js';

export interface CategoryTreeProps {
  section: LibrarySection;
  tree: LibraryTree;
}

/**
 * Form Categories, salt okunur (plan M8a Task 17; ADR 0037 §7, §11; ADR 0038 §9): gemide geminin
 * ağacı. Gemi ağacı değiştiremez; ağacı ofis düzenler. Kod, ad ve klasördeki belge sayısı; boş
 * canlı klasörler de görünür. Belge taşıyan emekli klasörler "Retired folders" altında, açıklamasıyla
 * ve salt okunur; klasörü bu ağaçta olmayan şirket belgeleri "Other company documents" satırında
 * sayılır. Hiçbir belge kaybolmaz.
 */
export function CategoryTree({ section, tree }: CategoryTreeProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.categories.${key}`, options ?? {});
  const language = i18n.language ?? 'en';
  const live = tree.folders.filter((f) => !f.retired);
  const retired = tree.folders.filter((f) => f.retired);
  const other = otherDocumentIds(tree).size;

  const label = (folder: LibraryFolder) =>
    localFolderLabel({ ...folder, nameTr: folder.nameTr ?? null }, language);

  const render = (
    pool: readonly LibraryFolder[],
    parentId: string | null,
    level: number,
    caption: string | undefined,
  ): ReactNode => {
    const ids = new Set(pool.map((f) => f.id));
    const siblings =
      parentId === null
        ? siblingsOf(
            pool.map((f) =>
              f.parentId !== null && !ids.has(f.parentId) ? { ...f, parentId: null } : f,
            ),
            null,
          )
        : siblingsOf(pool, parentId);
    if (siblings.length === 0) return null;
    return (
      <ul
        className={classes.catList}
        data-level={level}
        aria-label={level === 1 ? caption : undefined}
      >
        {siblings.map((folder) => {
          const text = label(folder);
          return (
            <li
              key={folder.id}
              className={classes.catItem}
              data-retired={folder.retired || undefined}
            >
              <div className={classes.catRow} style={{ paddingInlineStart: 8 + (level - 1) * 22 }}>
                <IconFolder size={18} stroke={1.7} className={classes.treeFolderIcon} aria-hidden />
                <span className={classes.catLabel} title={text}>
                  {text}
                </span>
                <span className={classes.catCount}>
                  {k('documents', { count: folderDocumentCount(tree, folder.id) })}
                </span>
              </div>
              {render(pool, folder.id, level + 1, caption)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Panel title={k('caption')} className={classes.catPanel}>
      <Text size="xs" c="dimmed" className={classes.catHint}>
        {k('readOnly')}
      </Text>
      {render(live, null, 1, `${k('caption')} – ${t(`documents.sections.${section}.title`)}`)}
      {retired.length > 0 ? (
        <section className={classes.catGroup} aria-label={k('retired')}>
          <h3 className={classes.catGroupTitle}>
            <IconArchive size={16} aria-hidden /> {k('retired')}
          </h3>
          <Text size="xs" c="dimmed" className={classes.catHint}>
            {k('retiredHint')}
          </Text>
          {render(retired, null, 1, k('retired'))}
        </section>
      ) : null}
      {other > 0 ? (
        <section className={classes.catGroup} aria-label={k('other')}>
          <div className={classes.catRow}>
            <IconFiles size={18} stroke={1.7} className={classes.treeFolderIcon} aria-hidden />
            <span className={classes.catLabel} title={k('otherHint')}>
              {k('other')}
            </span>
            <span className={classes.catCount}>{k('documents', { count: other })}</span>
          </div>
        </section>
      ) : null}
    </Panel>
  );
}
